import { action, observable } from 'mobx';
import { IButtonData, IMessageProps, Message } from './Message';
import {
  getErrorAsMessageObject,
  getErrorsByErrorCodes,
  getErrorsByModuleCode,
  getErrorWithoutStore
} from 'cd-eft/utils/getError';
import { ErrorHandlingResponse } from 'cd-eft/_codegen_/openapi-generator/eft-set';

interface NotificationProps {
  code: string;
  buttons?: IButtonData[];
  inputName?: string;
  canMute?: boolean;
  hasLocalMuteToggle?: boolean;
  callingElement?: string;
}

class MessageCenterVM {
  readonly mutedCodeStorageKey = 'mutedErrorCodes';
  @observable currentMessage?: Message;
  @observable pendingMessageQueue: Message[] = [];//unshift & pop
  @observable pendingMessageStack: Message[] = [];//push    & pop
  @observable messageCache?: ErrorHandlingResponse[] = [];
  private callsBlocked: boolean = false;
  private callWhitelist: string[] = [];
  private firstCallingElementId: string = '';

  @observable usingQueue: boolean = true;


  /**
  * Helper function to call the backend error handling api and return the short description as a promise
  * @param code Original Error Code used to retrieve the corresponding message
  * @returns
  */
  async getMessageFromCode(code: string): Promise<string> {
    const message = this.getFromCache(code);
    if (message !== undefined) {
      return message;
    } else {
      return getErrorWithoutStore(code);
    }
  }

  /**
   * Helper function to call the backend error handling api and return the Message object as a promise
   * @param code Original Error Code used to retrieve the corresponding message
   * @param severity The severity class of the notification.
   * @param buttons Optional. Array of button objects to add to the notification
   * @returns 
   */
  async getMessageFromCode_object(code: string, severity: 'success' | 'warn' | 'error' | 'default', buttons?: IButtonData[]): Promise<Message> {
    const messageResponseCache = this.getObjectFromCache(code);
    if (messageResponseCache !== undefined) {
      const messageData: IMessageProps = {
        title: messageResponseCache.errorCategoryCode!,
        messageContent: messageResponseCache.errorShortDescription!,
        severity: severity,
        type: 'notification',
        buttonArray: buttons,
        infoUrl: messageResponseCache.url,
        fullErrorCode: messageResponseCache.errorCode,
        shortErrorCode: messageResponseCache.originalErrorCode
      }
      return new Message(messageData);
    } else {
      const message = await getErrorAsMessageObject(code);
      const messageData: IMessageProps = {
        title: message.messageTitle,
        messageContent: message.messageContent,
        severity: severity,
        type: 'notification',
        buttonArray: buttons,
        infoUrl: message.infoUrl,
        fullErrorCode: message.fullErrorCode,
        shortErrorCode: code
      }
      return new Message(messageData)
    }
  }

  /**
   * Helper function fo call the backend error handling API and get all error messages in a module.
   * @param module The string code for the desired module.
   * @returns A promise array of ErrorHandlingResponse objects in the given module.
   */
  async getMessageSetFromModule(module: string) {
    return getErrorsByModuleCode(module);
  }

  /**
   * Helper function fo call the backend error handling API and get specific error messages.
   * @param codeString Single combined string containing all desired originalErrorCodes.
   * @returns A promise array of ErrorHandlingResponse objects.
   */
  async getMessageListFromErrorCodes(codeString: string) {
    return getErrorsByErrorCodes(codeString);
  }

  /**
   * Function to display a hardcoded notification with default styling.
   * @param message Message to display in the notification.
   * @param buttons Optional array of {@linkcode IButtonData} to display within the notification.
   */
  @action.bound addSuccess(message: string, buttons?: IButtonData[], shortErrorCode?: string) {
    this.currentMessage = undefined;
    const messageData: IMessageProps = {
      title: 'Success!',
      messageContent: message,
      severity: 'success',
      type: 'notification',
      buttonArray: buttons,
      shortErrorCode: shortErrorCode
    }
    this.updateMessageQueue(new Message(messageData));
  }

  /**
   * Function to display a hardcoded notification with default styling.
   * @param message Message to display in the notification.
   * @param buttons Optional array of {@linkcode IButtonData} to display within the notification.
   */
  @action.bound addInfo(message: string, buttons?: IButtonData[], shortErrorCode?: string) {
    this.currentMessage = undefined;
    const messageData: IMessageProps = {
      title: 'Alert!',
      messageContent: message,
      severity: 'default',
      type: 'notification',
      buttonArray: buttons,
      shortErrorCode: shortErrorCode
    }
    this.updateMessageQueue(new Message(messageData));
  }

  /**
   * Function to retrieve and display an info notification given an error code.
   * Notification will display with 'default' styling, title: Alert!, and the response message text.
   * @param code Error code used to retrieve a corresponding message.
   * @param buttons Optional array of {@linkcode IButtonData} to display within the notification.
   */
  @action.bound async addInfoFromCode(code: string, buttons?: IButtonData[]) {
    const message = await this.getMessageFromCode_object(code, 'default', buttons);
    this.updateMessageQueue(message);
  }

  /**
   * Function to display a hardcoded notification with 'warn' styling.
   * @param message Message to display in the notification.
   * @param buttons Optional array of {@linkcode IButtonData} to display within the notification.
   */
  @action.bound addWarning(message: string, buttons?: IButtonData[], shortErrorCode?: string) {
    const messageData: IMessageProps = {
      title: 'Warning!',
      messageContent: message,
      severity: 'warn',
      type: 'notification',
      buttonArray: buttons,
      shortErrorCode: shortErrorCode
    }
    this.updateMessageQueue(new Message(messageData));
  }

  /**
   * Function to retrieve and display a warning notification given an error code.
   * Notification will display with 'warn' styling, title: Warning!, and the response message text.
   * @param code Error code used to retrieve a corresponding message.
   * @param buttons Optional array of {@linkcode IButtonData} to display within the notification.
   */
  @action.bound async addWarningFromCode(code: string, buttons?: IButtonData[]) {
    const message = await this.getMessageFromCode_object(code, 'warn', buttons);
    this.updateMessageQueue(message);
  }

  /**
   * Function to display a hardcoded notification with 'error' styling.
   * @param message Message to display in the notification.
   * @param buttons Optional array of {@linkcode IButtonData} to display within the notification.
   */
  @action.bound addError(message: string, buttons?: IButtonData[], shortErrorCode?: string) {
    const messageData: IMessageProps = {
      title: 'Error!',
      messageContent: message,
      severity: 'error',
      type: 'notification',
      buttonArray: buttons,
      shortErrorCode: shortErrorCode
    }
    this.updateMessageQueue(new Message(messageData));
  }

  /**
   * Function to retrieve and display an error notification given an error code.
   * Notification will display with 'error' styling, title: Error!, and the response message text.
   * @param code Error code used to retrieve a corresponding message.
   * @param buttons Optional array of {@linkcode IButtonData} to display within the notification.
   */
  @action.bound async addErrorFromCode(code: string, buttons?: IButtonData[]) {
    const message = await this.getMessageFromCode_object(code, 'error', buttons);
    this.updateMessageQueue(message);
  }

  @action.bound addNotification(message: string, severity: string, shortErrorCode?: string, buttons?: IButtonData[],) {
    const messageData: IMessageProps = {
      title: `${severity}!`,
      messageContent: message,
      severity: severity.toLocaleLowerCase() as any,
      type: 'notification',
      buttonArray: buttons,
      shortErrorCode: shortErrorCode
    }
    this.updateMessageQueue(new Message(messageData));
  }
  /**
   * Retrieves an error message with an error code and creates a notification from the response.
   * Can display a notification of types: Info/Alert (default), Warning, Error depending on the response.
   * @param code Error code used to retrieve a corresponding message.
   * @param buttons Optional array of {@linkcode IButtonData} to display within the notification.
   * @param inputName Optional string name of the input the error is being shown for.
   * @param canMute Must be an Information notification. If true, this notification will have a mute checkbox.
   * @param hasLocalMuteToggle Must be an Information notification. If true, this notification will use a checkbox provided in buttons array. Otherwise will use session mute.
   */
  @action.bound async addNotificationFromCode(code: string, buttons?: IButtonData[], inputName?: string, canMute?: boolean, hasLocalMuteToggle?: boolean, callingElement?: any) {
    if (this.callsBlocked && !this.canICall(code)) {
      return;
    }
    if (sessionStorage.getItem(code) !== null) {
      return;
    }
    const message = await getErrorAsMessageObject(code, inputName, canMute ? canMute : false, callingElement);
    if (buttons) {
      message.buttonArray.push(...buttons);
    }
    if (hasLocalMuteToggle) {
      message.hasLocalMuteToggle = true;
    }
    this.updateMessageQueue(message);
  }
  /**
   * Alternative to addNotificationFromCode that uses an object rather than separate arguments.
   * Meant to update firstCallingElementId variable to return focus to the first element that called a notification in case of a queue.
   * @param props 
   */
  @action.bound async addNotificationFromCode_object(props: NotificationProps) {
    const { code, buttons, inputName, canMute, hasLocalMuteToggle, callingElement } = props;
    if (this.firstCallingElementId === '' && callingElement !== undefined && callingElement !== '') {
      this.firstCallingElementId = callingElement;
    }
    await this.addNotificationFromCode(code, buttons, inputName, canMute, hasLocalMuteToggle, callingElement);
  }

  /**
   * Creates and displays a modal given a message and a title. clears previoous message and sets currentMessage.
   * @param message Message to diplay in the body of the modal
   * @param title Title of the modal
   */
  @action.bound addModal(message: string, title: string) {
    // Will need expansion on modal functionality in the future. For now, this is very basic as a modal.
    const messageData: IMessageProps = {
      title: title,
      messageContent: message,
      severity: 'default',
      type: 'modal',
    }
    this.currentMessage = new Message(messageData);
  }

  /**
   * Function to get an error message from back-end and display as a modal.
   * Clears previous message and sets current message with modal options.
   * @param code Error code used to retrieve a corresponding message.
   */
  @action.bound async addModalFromCode(code: string) {
    // Will need expansion on modal functionality in the future. For now, this is very basic as a modal.
    const message = await this.getMessageFromCode(code);
    const messageData: IMessageProps = {
      title: '',
      messageContent: message,
      severity: 'default',
      type: 'modal'
    }
    this.currentMessage = new Message(messageData);
  }

  /**
   * Returns an error message string and clears currentMessage. Should be used as temporary hardcoded substitute for addErrorLabelFromCode if code not available.
   * @param string Error message to display
   * @returns
   */
  @action.bound addErrorLabel(string: string) {
    return string;
  }

  /**
   * Function to get an error message from Back-end for use in form input validation displays. Sets currentMessage to undefined.
   * @param code Error code used to retrieve a corresponding message.
   * @returns A string error message.
   */
  @action.bound async addErrorLabelFromCode(code: string) {
    return await this.getMessageFromCode(code);
  }

  /**
   * If there are messages waiting in Queue, pop from the End and assign to currentMessage. 
   * If nothing in Queue, set currentMessage to be undefined.
   * @param usedInNotification Should only be assigned as true if used in the default notification close buttons.
   */
  @action.bound clearMessages(usedInNotification?: boolean) {
    // Functionality here should be changed if/when we want multiple/stacked messages.
    // If we want a message stack/queue, we also need to change clearMessages calls in the add functions.

    // In case clearMessages was called without any messages actually being displayed, do nothing.
    // Condition required with addition of autofocus to #main. Many existing uses of clearMessages regardless of MC state.
    if ((this.currentMessage === undefined && this.pendingMessageQueue.length === 0) && !usedInNotification) {
      return
    }

    // check if muted
    if (this.currentMessage && this.currentMessage.muted) {
      this.saveMutedCodeToSession(this.currentMessage.shortErrorCode);
    }
    if (this.pendingMessageQueue.length > 0) {
      this.currentMessage = this.pendingMessageQueue.pop();
      this.currentMessage!.visible = true;
    }
    else {
      this.currentMessage = undefined;
      if (this.firstCallingElementId !== '') {
        document.getElementById(this.firstCallingElementId)?.focus();
        this.firstCallingElementId = '';
      }
      else {
        document.getElementById("main")?.focus();
      }
    }
  }

  /**
   * Clears current message and any pending messages.
   */
  @action.bound resetMessageCenter() {
    if (this.pendingMessageQueue.length > 0) {
      this.pendingMessageQueue = [];
    }
    this.clearMessages();
  }

  /**
   * Saves an error code to sessionStorage with the code as the key and a value of '1'.
   * @param code Short error code to save into sessionStorage
   */
  @action.bound saveMutedCodeToSession(code: string) {
    sessionStorage.setItem(code, '1');
  }

  /**
   * Blocks MessageCenter calls for all error codes not in the provided whitelist.
   * This should only be used temporarily and should always be manually unset within the same class/function.
   * @param whitelist string list of error codes we want to allow through the blocker.
   */
  @action.bound blockCalls(whitelist: string[]) {
    this.callWhitelist = whitelist;
    this.callsBlocked = true;
  }
  /**
   * Clears the whitelist and unblocks calls. Returns MessageCenter to normal.
   */
  @action.bound unblockCalls() {
    this.callWhitelist = [];
    this.callsBlocked = false;
  }
  /**
   * Checks provided error code for if it's included in the whitelist.
   * @param code 
   * @returns True if included in the whitelist, false if not.
   */
  private canICall(code: string) {
    return this.callWhitelist.includes(code);
  }

  /**
   * Gets all error messages of a specified module given the module code. Messages are stored in an array of type ErrorHandlingResponse.
   * @param module_code Error handling module code: STR, TR20, PROJ, SET, HTTP.
   */
  @action.bound async fillMessageCacheByModule(module_code: string) {
    this.messageCache = await this.getMessageSetFromModule(module_code);
  }

  /**
   * Gets only the supplied error codes from backend to store in memory.
   * @param error_codes Array of originalErrorCodes.
   */
  @action.bound async fillMessageCacheByOriginalCodes(error_codes: string[]) {
    const singleString = error_codes.join(',');
    const response = await this.getMessageListFromErrorCodes(singleString);
    this.validateResponseList(response);
    this.messageCache = response;
  }

  /**
   * Retrieves an error message from the array of pre-fetched messages of a module.
   * @param code Original Error Code for the desired error message.
   * @returns The errorShortDescription as a string it it exists, otherwise undefined.
   */
  getFromCache(code: string): string | undefined {
    if (this.messageCache && this.messageCache.length !== 0) {
      const index = this.messageCache?.findIndex((error) => error.originalErrorCode === code);
      if (index !== -1) {
        if (
          this.messageCache[index].errorShortDescription === undefined ||
          this.messageCache[index].errorShortDescription === null
        ) {
          // Case where a bad error code was requested -> object returned in array, likely due to code being removed in DB.
          // This error case should be resolved before deployment.
          // Result will log as error in console, actual message will be requested individually.
          console.error(`${code} : ${this.messageCache[index].errorHandlingFailure}`);
          return undefined;
        } else {
          // return this.messageCache[index].errorShortDescription!;
          return this.messageCache[index].errorShortDescription;
        }
      }
    }
    return undefined;
  }

  /**
   * Retrieves an error message from the array of pre-fetched messages.
   * @param code Original Error Code for the desired error message.
   * @returns The  ErrorHandlingResponse as an object if it exists, otherwise undefined.
   */
  getObjectFromCache(code: string): ErrorHandlingResponse | undefined {
    if (this.messageCache && this.messageCache.length !== 0) {
      const index = this.messageCache?.findIndex((error) => error.originalErrorCode === code);
      if (index !== -1) {
        if (
          this.messageCache[index].errorShortDescription === undefined ||
          this.messageCache[index].errorShortDescription === null
        ) {
          // Case where a bad error code was requested -> object returned in array, likely due to code being removed in DB.
          // This error case should be resolved before deployment.
          // Result will log as error in console, actual message will be requested individually.
          console.error(`${code} : ${this.messageCache[index].errorHandlingFailure}`);
          return undefined;
        } else {
          return this.messageCache[index];
        }
      }
    }
    return undefined;
  }

  private validateResponseList(list: ErrorHandlingResponse[]) {
    list.forEach((data) => {
      if (data.errorShortDescription === null || data.errorShortDescription === undefined) {
        console.error(`${data.originalErrorCode} : ${data.errorHandlingFailure}`);
      }
    });
  }

  /**
   * If there is no currentMessage, will set as the provided message. If currentMessage !== undefined, will add the 
   * provided message to the start of the pendingMessageQueue array.
   * @param {Message} newMessage Message object to be added to the message Queue.
   */
  @action.bound updateMessageQueue(newMessage: Message) {
    if (this.currentMessage === undefined) {
      // adding to queue and nothing displayed yet
      this.currentMessage = newMessage;
    }
    else {
      // something currently displayed/not cleared
      this.pendingMessageQueue.unshift(newMessage);
    }
  }
}

const MessageCenter = new MessageCenterVM();
export default MessageCenter;
